import { useApolloClient } from "@apollo/client";
import { getDataOrNull, PDF_UNITS, type PdfUnit } from "@msys/common";
import {
  CardContainer,
  Modal,
  ModalActions,
  ModalContent,
  ModalDialog,
  ModalOpenButton,
  ModalTitle,
  RadioGroupOption,
  useScreenWidth,
} from "@msys/ui";
import { Replay as ReplayIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { omit, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  PdfLetterheadSettings,
  PdfVisibility,
} from "../../../../clients/graphqlTypes.js";
import { AttachmentImageDragAndDropField } from "../../../commons/form-fields/AttachmentImageDragAndDropField.js";
import { ColorPickerField } from "../../../commons/form-fields/ColorPickerField.js";
import { FormattedFloatField } from "../../../commons/form-fields/FormattedFloatField.js";
import { RadioGroupField } from "../../../commons/form-fields/RadioGroupField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { FileInput } from "../../../commons/inputs/FileInput.js";
import { Attachment } from "../../attachments/helpers.js";
import {
  OrganisationPdfSettingsBox_PdfLetterheadFragment,
  useGenerateExamplePdfDocumentMutation,
  useGenerateOrganisationPdfLetterheadModalQuery,
  useGenerateOrganisationPdfLetterheadMutation,
  useRequestOrganisationPdfLetterheadUploadUrlMutation,
  useSetOrganisationPdfLetterheadMutation,
} from "./OrganisationPdfSettingsBox.generated.js";
import { resolveLetterheadFormInitialSettings } from "./letterheadFormHelpers.js";

// --- Margin parsing helpers (inline since @msys/pdf-utils is server-only) ---

const MARGIN_REGEX = /^(\d+(?:\.\d+)?)(pt|mm|cm|in)$/;

const POINTS_PER_UNIT: Record<PdfUnit, number> = {
  pt: 1,
  mm: 2.83465,
  cm: 28.3465,
  in: 72,
};

function parseMargin(margin: string): { value: number; unit: PdfUnit } {
  const match = margin.match(MARGIN_REGEX);
  if (!match) {
    return { value: 0, unit: "pt" };
  }
  return { value: parseFloat(match[1]), unit: match[2] as PdfUnit };
}

function toPoints(margin: string): number {
  const parsed = parseMargin(margin);
  return parsed.value * POINTS_PER_UNIT[parsed.unit];
}

function formatMargin(value: number, unit: PdfUnit): string {
  return `${value}${unit}`;
}

// --- Composite margin field: number input + unit dropdown ---

const MarginField = ({
  valueName,
  unitName,
  label,
}: {
  valueName: string;
  unitName: string;
  label: string;
}) => {
  const { t } = useTranslate(["Quote"]);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          flex: 2,
        }}
      >
        <FormattedFloatField name={valueName} label={label} min={0} />
      </Box>
      <Box
        sx={{
          flex: 1,
        }}
      >
        <FormControl sx={{ minWidth: 80 }}>
          <InputLabel>{t("Unit", { ns: "Quote" })}</InputLabel>
          <UnitSelect name={unitName} />
        </FormControl>
      </Box>
    </Stack>
  );
};

const UnitSelect = ({ name }: { name: string }) => {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const { value } = getFieldProps<PdfUnit>(name);

  return (
    <Select
      value={value}
      onChange={async e => {
        await setFieldValue(name, e.target.value as PdfUnit);
      }}
      size="small"
      sx={{ minWidth: 70 }}
    >
      {PDF_UNITS.map(unit => (
        <MenuItem key={unit} value={unit}>
          {unit}
        </MenuItem>
      ))}
    </Select>
  );
};

interface Props {
  organisationPdfLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment | null;
}

export function OrganisationPdfSettingsBox({
  organisationPdfLetterhead,
}: Props) {
  const { t } = useTranslate("OrganisationSettings");
  return (
    <CardContainer isExpandable title={t("General PDF settings")}>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          padding: 1,
        }}
      >
        {organisationPdfLetterhead?.attachment.url && (
          <>
            <Link
              component={"a"}
              href={organisationPdfLetterhead.attachment.url}
              target="_blank"
            >
              {t("Letterhead", { ns: "OrganisationSettings" })}
            </Link>
            <ModalOpenButton
              Modal={EditLetterheadModal}
              modalProps={{
                url: organisationPdfLetterhead?.attachment.url,
                pdfLetterheadSettings: organisationPdfLetterhead.settings,
              }}
            >
              <Button variant="text" color="secondary">
                {t("Edit letterhead", { ns: "OrganisationSettings" })}
              </Button>
            </ModalOpenButton>
          </>
        )}
        <ModalOpenButton
          Modal={CreateNewPdfLetterheadModal}
          modalProps={{ organisationPdfLetterhead }}
        >
          <Button variant="text" color="secondary">
            {t("Create new letterhead", { ns: "OrganisationSettings" })}
          </Button>
        </ModalOpenButton>
      </Stack>
    </CardContainer>
  );
}

const EditLetterheadModal = ({
  handleClose,
  url,
  pdfLetterheadSettings,
}: {
  handleClose: () => void;
  url: string;
  pdfLetterheadSettings: PdfLetterheadSettings;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);

  return (
    <ModalDialog handleClose={handleClose} maxWidth={url ? "md" : "sm"}>
      <ModalTitle
        title={t("Edit letterhead", { ns: "OrganisationSettings" })}
        handleClose={handleClose}
      />
      <LetterheadSettingsForm
        url={url}
        {...resolveLetterheadFormInitialSettings({
          existing: pdfLetterheadSettings,
        })}
        handleComplete={handleClose}
      />
    </ModalDialog>
  );
};

const CreateNewPdfLetterheadModal = ({
  handleClose,
  organisationPdfLetterhead,
}: {
  handleClose: () => void;
  organisationPdfLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment | null;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const [url, setUrl] = React.useState<string | null>(null);
  const [generatedColors, setGeneratedColors] = React.useState<{
    textPrimaryColor: string;
    textSecondaryColor: string;
  } | null>(null);

  const client = useApolloClient();
  const [requestUploadUrl] =
    useRequestOrganisationPdfLetterheadUploadUrlMutation({
      client,
    });

  return (
    <ModalDialog handleClose={handleClose} maxWidth={url ? "md" : "sm"}>
      <ModalTitle
        title={t("Create new letterhead", { ns: "OrganisationSettings" })}
        handleClose={handleClose}
      />
      {url ? (
        <LetterheadSettingsForm
          url={url}
          {...resolveLetterheadFormInitialSettings({
            existing: organisationPdfLetterhead?.settings,
            generated: generatedColors,
          })}
          handleComplete={handleClose}
        />
      ) : (
        <ModalContent>
          <Stack direction={"column"} spacing={1}>
            <Button
              color="secondary"
              onClick={() => fileUploadRef.current?.click()}
            >
              {t("Upload", { ns: "Global" })}
            </Button>
            <FileInput
              innerRef={fileUploadRef}
              multiple={false}
              accept="application/pdf"
              onComplete={async file => {
                try {
                  const response = await requestUploadUrl();
                  const uploadUrl =
                    response.data?.requestOrganisationPdfLetterheadUploadUrl
                      .uploadUrl;
                  const accessUrl =
                    response.data?.requestOrganisationPdfLetterheadUploadUrl
                      .accessUrl;
                  if (!uploadUrl) throw new Error("Upload URL is missing");
                  if (!accessUrl) throw new Error("Access URL is missing");

                  await fetch(uploadUrl, {
                    method: "PUT",
                    body: file,
                  });
                  // An uploaded letterhead has no generate-step colors;
                  // clear any stale ones so they aren't applied to it.
                  setGeneratedColors(null);
                  setUrl(accessUrl);
                } catch (error) {
                  console.error(error);
                }
              }}
            />

            <Divider>
              <Typography sx={{ color: theme => theme.palette.grey[400] }}>
                {t("or", { ns: "Global" })}
              </Typography>
            </Divider>

            <ModalOpenButton
              Modal={GenerateOrganisationPdfLetterheadModal}
              modalProps={{
                existingLetterhead: organisationPdfLetterhead,
                handleComplete: async (url, colors) => {
                  // Set the colors before the URL: the URL turning truthy
                  // mounts LetterheadSettingsForm, whose Formik initialValues
                  // do not reinitialize, so the colors must already be set.
                  setGeneratedColors(colors);
                  setUrl(url);
                },
              }}
            >
              <Button variant="text" color="secondary">
                {t("Generate", { ns: "Global" })}
              </Button>
            </ModalOpenButton>
          </Stack>
        </ModalContent>
      )}
    </ModalDialog>
  );
};

interface LetterheadSettingsFormValues {
  marginTopFirstPageValue: number;
  marginTopFirstPageUnit: PdfUnit;
  marginTopSubsequentPageValue: number;
  marginTopSubsequentPageUnit: PdfUnit;
  marginBottomValue: number;
  marginBottomUnit: PdfUnit;
  marginLeftValue: number;
  marginLeftUnit: PdfUnit;
  marginRightValue: number;
  marginRightUnit: PdfUnit;
  primaryColor: string;
  secondaryColor: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
}

function getMarginValues(
  margin: string | undefined,
  defaultValue: number,
  defaultUnit: PdfUnit
): { value: number; unit: PdfUnit } {
  if (!margin) return { value: defaultValue, unit: defaultUnit };
  const parsed = parseMargin(margin);
  return { value: parsed.value, unit: parsed.unit };
}

function formValuesToSubmitValues(values: LetterheadSettingsFormValues) {
  return {
    marginTopFirstPage: formatMargin(
      values.marginTopFirstPageValue,
      values.marginTopFirstPageUnit
    ),
    marginTopSubsequentPage: formatMargin(
      values.marginTopSubsequentPageValue,
      values.marginTopSubsequentPageUnit
    ),
    marginBottom: formatMargin(
      values.marginBottomValue,
      values.marginBottomUnit
    ),
    marginLeft: formatMargin(values.marginLeftValue, values.marginLeftUnit),
    marginRight: formatMargin(values.marginRightValue, values.marginRightUnit),
    primaryColor: values.primaryColor,
    secondaryColor: values.secondaryColor,
    textPrimaryColor: values.textPrimaryColor,
    textSecondaryColor: values.textSecondaryColor,
  };
}

const LetterheadSettingsForm = ({
  url,
  marginTopFirstPage,
  marginTopSubsequentPage,
  marginBottom,
  marginLeft,
  marginRight,
  primaryColor,
  secondaryColor,
  textPrimaryColor,
  textSecondaryColor,
  handleComplete,
}: {
  url: string;
  marginTopFirstPage?: string | undefined;
  marginTopSubsequentPage?: string | undefined;
  marginBottom?: string | undefined;
  marginLeft?: string | undefined;
  marginRight?: string | undefined;
  primaryColor?: string | undefined;
  secondaryColor?: string | undefined;
  textPrimaryColor?: string | undefined;
  textSecondaryColor?: string | undefined;
  handleComplete: () => void;
}) => {
  const { isMaxPhone } = useScreenWidth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationBranding",
    "Global",
  ]);

  const [exampleDocumentUrl, setExampleDocumentUrl] = React.useState<
    string | null
  >(null);

  const client = useApolloClient();
  const [generateExampleDocument] = useGenerateExamplePdfDocumentMutation({
    client,
  });
  const [setLetterhead] = useSetOrganisationPdfLetterheadMutation({
    client,
  });

  const initialValues = React.useMemo((): LetterheadSettingsFormValues => {
    const topFirst = getMarginValues(marginTopFirstPage, 50, "mm");
    const topSubsequent = getMarginValues(marginTopSubsequentPage, 20, "mm");
    const bottom = getMarginValues(marginBottom, 26, "mm");
    const left = getMarginValues(marginLeft, 20, "mm");
    const right = getMarginValues(marginRight, 10, "mm");

    return {
      marginTopFirstPageValue: topFirst.value,
      marginTopFirstPageUnit: topFirst.unit,
      marginTopSubsequentPageValue: topSubsequent.value,
      marginTopSubsequentPageUnit: topSubsequent.unit,
      marginBottomValue: bottom.value,
      marginBottomUnit: bottom.unit,
      marginLeftValue: left.value,
      marginLeftUnit: left.unit,
      marginRightValue: right.value,
      marginRightUnit: right.unit,
      primaryColor: primaryColor ?? "#001e64",
      secondaryColor: secondaryColor ?? "#377dc3",
      textPrimaryColor: textPrimaryColor ?? "#000000",
      textSecondaryColor: textSecondaryColor ?? "#000000",
    };
  }, [
    marginBottom,
    marginLeft,
    marginRight,
    marginTopFirstPage,
    marginTopSubsequentPage,
    primaryColor,
    secondaryColor,
    textPrimaryColor,
    textSecondaryColor,
  ]);

  const validationSchema = Yup.object().shape({
    marginTopFirstPageValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Top margin on first page", { ns: "OrganisationSettings" }) +
          " must be at most 500pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginTopFirstPageUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 500;
        }
      )
      .label(t("Top margin on first page", { ns: "OrganisationSettings" })),
    marginTopFirstPageUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginTopSubsequentPageValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Top margin on subsequent pages", { ns: "OrganisationSettings" }) +
          " must be at most 350pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginTopSubsequentPageUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 350;
        }
      )
      .label(
        t("Top margin on subsequent pages", { ns: "OrganisationSettings" })
      ),
    marginTopSubsequentPageUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginBottomValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Bottom margin", { ns: "OrganisationSettings" }) +
          " must be at most 200pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginBottomUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 200;
        }
      )
      .label(t("Bottom margin", { ns: "OrganisationSettings" })),
    marginBottomUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginLeftValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Left margin", { ns: "OrganisationSettings" }) +
          " must be at most 200pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginLeftUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 200;
        }
      )
      .label(t("Left margin", { ns: "OrganisationSettings" })),
    marginLeftUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginRightValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Right margin", { ns: "OrganisationSettings" }) +
          " must be at most 200pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginRightUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 200;
        }
      )
      .label(t("Right margin", { ns: "OrganisationSettings" })),
    marginRightUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    primaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Primary color", { ns: "OrganisationSettings" })),
    secondaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Secondary color", { ns: "OrganisationSettings" })),
    textPrimaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text primary color", { ns: "OrganisationSettings" })),
    textSecondaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text secondary color", { ns: "OrganisationSettings" })),
  });

  const initialSubmitValues = React.useMemo(
    () => formValuesToSubmitValues(initialValues),
    [initialValues]
  );

  React.useEffect(() => {
    generateExampleDocument({
      variables: {
        input: {
          pdfLetterheadUrl: url,
          marginTopFirstPage: initialSubmitValues.marginTopFirstPage,
          marginTopSubsequentPage: initialSubmitValues.marginTopSubsequentPage,
          marginBottom: initialSubmitValues.marginBottom,
          marginLeft: initialSubmitValues.marginLeft,
          marginRight: initialSubmitValues.marginRight,
          textPrimaryColor: initialSubmitValues.textPrimaryColor,
          textSecondaryColor: initialSubmitValues.textSecondaryColor,
          primaryColor: initialSubmitValues.primaryColor,
          secondaryColor: initialSubmitValues.secondaryColor,
        },
      },
    }).then(({ data }) => {
      setExampleDocumentUrl(data?.generateExamplePdfDocument.url ?? null);
    });
  }, [generateExampleDocument, initialSubmitValues, url]);

  const handleSubmit = async (
    values: LetterheadSettingsFormValues,
    formikHelpers: FormikHelpers<LetterheadSettingsFormValues>
  ) => {
    const submitValues = formValuesToSubmitValues(values);
    generateExampleDocument({
      variables: {
        input: {
          pdfLetterheadUrl: url,
          marginTopFirstPage: submitValues.marginTopFirstPage,
          marginTopSubsequentPage: submitValues.marginTopSubsequentPage,
          marginBottom: submitValues.marginBottom,
          marginLeft: submitValues.marginLeft,
          marginRight: submitValues.marginRight,
          textPrimaryColor: submitValues.textPrimaryColor,
          textSecondaryColor: submitValues.textSecondaryColor,
          primaryColor: submitValues.primaryColor,
          secondaryColor: submitValues.secondaryColor,
        },
      },
    }).then(({ data }) => {
      setExampleDocumentUrl(data?.generateExamplePdfDocument.url ?? null);
      formikHelpers.resetForm({ values });
    });
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<LetterheadSettingsFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {formikProps => (
        <>
          <ModalContent>
            <Form id={formId}>
              <Stack
                direction={isMaxPhone ? "column-reverse" : "row"}
                spacing={1}
              >
                {exampleDocumentUrl && (
                  <iframe
                    style={{ border: "none" }}
                    src={exampleDocumentUrl}
                    width="100%"
                    height="1100px"
                  />
                )}
                <Divider
                  orientation={isMaxPhone ? "horizontal" : "vertical"}
                  flexItem
                />
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    minWidth: 300,
                  }}
                >
                  <Typography variant="h4">
                    {t("Content settings", { ns: "OrganisationSettings" })}
                  </Typography>
                  <MarginField
                    valueName="marginTopFirstPageValue"
                    unitName="marginTopFirstPageUnit"
                    label={t("Top margin on first page", {
                      ns: "OrganisationSettings",
                    })}
                  />
                  <MarginField
                    valueName="marginTopSubsequentPageValue"
                    unitName="marginTopSubsequentPageUnit"
                    label={t("Top margin on subsequent pages", {
                      ns: "OrganisationSettings",
                    })}
                  />
                  <MarginField
                    valueName="marginBottomValue"
                    unitName="marginBottomUnit"
                    label={t("Bottom margin", { ns: "OrganisationSettings" })}
                  />
                  <MarginField
                    valueName="marginLeftValue"
                    unitName="marginLeftUnit"
                    label={t("Left margin", { ns: "OrganisationSettings" })}
                  />
                  <MarginField
                    valueName="marginRightValue"
                    unitName="marginRightUnit"
                    label={t("Right margin", { ns: "OrganisationSettings" })}
                  />
                  <Divider />
                  <ColorPickerField
                    label={t("Primary color", {
                      ns: "OrganisationSettings",
                    })}
                    name="primaryColor"
                    required
                  />
                  <ColorPickerField
                    label={t("Secondary color", {
                      ns: "OrganisationSettings",
                    })}
                    name="secondaryColor"
                    required
                  />
                  <Divider />
                  <ColorPickerField
                    label={t("Text primary color", {
                      ns: "OrganisationSettings",
                    })}
                    name="textPrimaryColor"
                    required
                  />
                  <ColorPickerField
                    label={t("Text secondary color", {
                      ns: "OrganisationSettings",
                    })}
                    name="textSecondaryColor"
                    required
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ReplayIcon />}
                    type="submit"
                    disabled={!formikProps.dirty || !formikProps.isValid}
                  >
                    {t("Update preview", { ns: "OrganisationSettings" })}
                  </Button>
                </Stack>
              </Stack>
            </Form>
          </ModalContent>
          <ModalActions
            actionButtons={[
              {
                label: t("Confirm", { ns: "Global" }),
                buttonProps: {
                  type: "submit",
                  form: formId,
                  disabled: formikProps.dirty,
                },
                handleClick: async () => {
                  try {
                    const submitValues = formValuesToSubmitValues(
                      formikProps.values
                    );
                    await setLetterhead({
                      variables: {
                        input: {
                          pdfLetterheadUrl: url,
                          ...submitValues,
                        },
                      },
                    });
                    handleComplete();
                  } catch (error) {
                    if (error instanceof Error)
                      enqueueSnackbar(error.message, { variant: "error" });
                  }
                },
              },
            ]}
          />
        </>
      )}
    </Formik>
  );
};

interface FormValues {
  pdfFooterColumn1: string;
  pdfFooterColumn2: string;
  pdfFooterColumn3: string;
  pdfFooterVisibility: PdfVisibility;
  pdfLogo: Attachment | null;
  pdfTextPrimaryColor: string;
  pdfTextSecondaryColor: string;
}

const GenerateOrganisationPdfLetterheadModal = ({
  handleClose,
  handleComplete,
  existingLetterhead,
}: {
  handleClose: () => void;
  handleComplete: (
    url: string,
    colors: { textPrimaryColor: string; textSecondaryColor: string }
  ) => Promise<void>;
  existingLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment | null;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationBranding",
    "Global",
  ]);

  const client = useApolloClient();
  const query = useGenerateOrganisationPdfLetterheadModalQuery({ client });
  const organisationPdfSettings = getDataOrNull(
    query.data?.organisationPdfSettings
  );
  const [generateLetterhead] = useGenerateOrganisationPdfLetterheadMutation({
    client,
  });

  const initialValues = React.useMemo((): FormValues => {
    if (organisationPdfSettings) {
      return {
        pdfFooterColumn1: organisationPdfSettings.pdfFooterColumn1,
        pdfFooterColumn2: organisationPdfSettings.pdfFooterColumn2,
        pdfFooterColumn3: organisationPdfSettings.pdfFooterColumn3,
        pdfFooterVisibility: organisationPdfSettings.pdfFooterVisibility,
        // Prefill text colors from the existing letterhead when one exists
        // (its stored colors are the active ones used in rendered PDFs),
        // falling back to the organisation settings. Logo and footer are not
        // part of the letterhead record, so they stay sourced from settings.
        pdfTextPrimaryColor:
          existingLetterhead?.settings.textPrimaryColor ??
          organisationPdfSettings.pdfTextPrimaryColor,
        pdfTextSecondaryColor:
          existingLetterhead?.settings.textSecondaryColor ??
          organisationPdfSettings.pdfTextSecondaryColor,
        pdfLogo: organisationPdfSettings.pdfLogo ?? null,
      };
    }

    return {
      pdfFooterColumn1: "",
      pdfFooterColumn2: "",
      pdfFooterColumn3: "",
      pdfFooterVisibility: "no",
      pdfLogo: null,
      pdfTextPrimaryColor: "",
      pdfTextSecondaryColor: "",
    };
  }, [organisationPdfSettings, existingLetterhead]);

  const validationSchema = Yup.object().shape({
    pdfFooterColumn1: Yup.string().label(
      t("Column A", { ns: "OrganisationSettings" })
    ),
    pdfFooterColumn2: Yup.string().label(
      t("Column B", { ns: "OrganisationSettings" })
    ),
    pdfFooterColumn3: Yup.string().label(
      t("Column C", { ns: "OrganisationSettings" })
    ),
    pdfTextPrimaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text primary color", { ns: "OrganisationSettings" })),
    pdfTextSecondaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text secondary color", { ns: "OrganisationSettings" })),
    pdfFooterVisibility: Yup.string().required(),
    pdfLogo: Yup.object().nullable(),
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const input = {
        pdfFooterColumn1: values.pdfFooterColumn1,
        pdfFooterColumn2: values.pdfFooterColumn2,
        pdfFooterColumn3: values.pdfFooterColumn3,
        pdfFooterVisibility: values.pdfFooterVisibility,
        pdfTextPrimaryColor: values.pdfTextPrimaryColor,
        pdfTextSecondaryColor: values.pdfTextSecondaryColor,
        pdfLogo: values.pdfLogo
          ? omit(values.pdfLogo, "id", "__typename")
          : null,
      };
      const { data } = await generateLetterhead({
        variables: {
          input,
        },
      });
      const url = data?.generateOrganisationPdfLetterhead.url;
      if (!url) throw new Error("URL is missing");
      await handleComplete(url, {
        textPrimaryColor: values.pdfTextPrimaryColor,
        textSecondaryColor: values.pdfTextSecondaryColor,
      });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  if (query.loading) return null;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      <Form id={formId}>
        <Modal
          title={t("Generate PDF letterhead", { ns: "OrganisationSettings" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Generate", { ns: "Global" }),
              buttonProps: { type: "submit", form: formId },
            },
          ]}
          isLoading={query.loading}
        >
          <Stack direction="column" spacing={1}>
            <AttachmentImageDragAndDropField
              label={t("Logo in header", { ns: "OrganisationSettings" })}
              name="pdfLogo"
            />

            <Divider />
            <Typography>
              {t("Page colors", { ns: "OrganisationSettings" })}
            </Typography>

            <ColorPickerField
              label={t("Text primary color", { ns: "OrganisationSettings" })}
              name="pdfTextPrimaryColor"
              required
            />
            <ColorPickerField
              label={t("Text secondary color", { ns: "OrganisationSettings" })}
              name="pdfTextSecondaryColor"
              required
            />

            <Divider />

            <Typography>
              {t("Page footer", { ns: "OrganisationSettings" })}
            </Typography>
            <PdfVisibilityField name="pdfFooterVisibility" />

            <TextField
              name="pdfFooterColumn1"
              label={t("Column A", { ns: "OrganisationSettings" })}
              multiline
              disabled={false}
              rows={3}
              helperText={t(
                "Eg. address, contact phone, contact email address etc.",
                { ns: "OrganisationSettings" }
              )}
            />

            <TextField
              name="pdfFooterColumn2"
              label={t("Column B", { ns: "OrganisationSettings" })}
              multiline
              disabled={false}
              rows={3}
              helperText={t(
                "Eg. tax ID, registration office, managing director etc.",
                { ns: "OrganisationSettings" }
              )}
            />

            <TextField
              name="pdfFooterColumn3"
              label={t("Column C", { ns: "OrganisationSettings" })}
              multiline
              disabled={false}
              rows={3}
              helperText={t("Eg. bank account, bank name, BIC code etc.", {
                ns: "OrganisationSettings",
              })}
            />
          </Stack>
        </Modal>
      </Form>
    </Formik>
  );
};

const usePdfVisibilityTranslation = () => {
  const { t } = useTranslate("PdfVisibilityField");

  return (pdfVisibility: PdfVisibility) => {
    switch (pdfVisibility) {
      case "all_pages":
        return t("On all pages");
      case "first_page":
        return t("Only on the first page");
      case "no":
        return t("None");
    }
  };
};

const ALL_PDF_VISIBILITY_TYPES: PdfVisibility[] = [
  "all_pages",
  "first_page",
  "no",
];

const PdfVisibilityField = (props: { name: string }) => {
  const t = usePdfVisibilityTranslation();
  const options: RadioGroupOption[] = ALL_PDF_VISIBILITY_TYPES.map(
    pdfVisibility => ({
      label: t(pdfVisibility), // FIXME: don't call t with variable, scanner will not detect these values
      value: pdfVisibility,
    })
  );
  return <RadioGroupField {...props} options={options} condensed />;
};
